import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { furnitureService, FurnitureData } from '../../services/furniture';
import { useAuth } from '../../contexts/AuthContext';
import EduBadge from '../../components/EduBadge';
import { getNeighborhoodLabel } from '../../constants/neighborhoods';
import { savedService } from '../../services/saved';
import axios from 'axios';
import { getApiUrl } from '../../services/apiBase';

const FurnitureDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<FurnitureData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  
  // Saved items state
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [savingItem, setSavingItem] = useState<boolean>(false);
  
  useEffect(() => {
    if (user) savedService.list().then(items => setIsSaved(items.some(item => item._id === id))).catch(console.error);
    else setIsSaved(false);
  }, [id, user]);

  useEffect(() => {
    const fetchItem = async () => {
      if (!id) {
        setError('furniture.detail.noItemId');
        setLoading(false);
        return;
      }
      
      try {
        const response = await furnitureService.getFurnitureById(id);
        setItem(response.furniture);
      } catch (error) {
        console.error('Error fetching furniture item:', error);
        setError('furniture.detail.notFound');
      } finally {
        setLoading(false);
      }
    };
    
    fetchItem();
  }, [id]);

  const [showContact, setShowContact] = useState<boolean>(false);
  const [contactWechat, setContactWechat] = useState('');

  useEffect(() => {
    setContactWechat('');
    if (!item?._id || !user?.eduVerified) return;
    furnitureService.getSellerContact(item._id)
      .then(setContactWechat)
      .catch(() => setContactWechat(''));
  }, [item?._id, user?.eduVerified]);

  const handleContact = () => {
    const sellerId =
      item && typeof item.seller === 'object' && item.seller
        ? item.seller._id
        : typeof item?.seller === 'string'
          ? item.seller
          : '';

    if (sellerId && user) {
      navigate(`/buyer/messages?to=${sellerId}&furniture=${item?._id || ''}`);
      return;
    }

    if (sellerId && !user) {
      navigate('/login', { state: { from: { pathname: `/buyer/messages`, search: `?to=${sellerId}&furniture=${item?._id || ''}` } } });
      return;
    }

    setShowContact(true);
  };

  // Seller may be a populated object or a bare id string
  const sellerInfo =
    item && typeof item.seller === 'object' && item.seller !== null
      ? item.seller
      : null;

  const handleCopyWechat = async () => {
    if (!contactWechat) return;
    try {
      await navigator.clipboard.writeText(contactWechat);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Clipboard copy failed', error);
    }
  };
  
  const handleSaveItem = async () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/furniture/${id}` } } });
      return;
    }
    setSavingItem(true);
    try {
      if (isSaved) {
        await savedService.remove(id!);
        setIsSaved(false);
      } else {
        await savedService.save(id!);
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Failed to save listing', error);
    } finally {
      setSavingItem(false);
    }
  };

  const handleReport = async () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/furniture/${id}` } } });
      return;
    }
    const reason = window.prompt(t('furniture.detail.reportReason'));
    if (reason === null) return;
    if (reason.trim().length < 10) {
      alert(t('furniture.detail.reportTooShort'));
      return;
    }
    try {
      await axios.post(`${getApiUrl()}/reports`, { listingId: id, reason }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      alert(t('furniture.detail.reportSent'));
    } catch (error: any) {
      alert(error.response?.data?.message || t('furniture.detail.reportFailed'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t(error || 'furniture.detail.notFound')}</h1>
          <Link
            to="/furniture"
            className="text-indigo-600 hover:text-indigo-500 font-medium"
          >
            ← {t('furniture.detail.backToListings')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <Link to="/" className="text-gray-500 hover:text-gray-700">
                {t('navbar.home')}
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-gray-300"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                </svg>
                <Link to="/furniture" className="ml-4 text-gray-500 hover:text-gray-700">
                  {t('furniture.listing.title')}
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-gray-300"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                </svg>
                <span className="ml-4 text-gray-500" aria-current="page">{item.title}</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="lg:grid lg:grid-cols-2 lg:gap-x-8 mt-8">
          {/* Image */}
          <div className="lg:max-w-lg lg:self-start">
            <div className="rounded-lg overflow-hidden">
              <img
                src={item.images && item.images[0] ? item.images[0] : '/placeholder-image.jpg'}
                alt={item.title}
                className="w-full h-full object-center object-cover"
              />
            </div>
          </div>

          {/* Product info */}
          <div className="mt-10 px-4 sm:px-0 sm:mt-16 lg:mt-0">
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">{item.title}</h1>
            
            <div className="mt-3">
              <h2 className="sr-only">{t('furniture.detail.productInfo')}</h2>
              <p className="text-3xl text-gray-900">${item.price}</p>
            </div>

            <div className="mt-4">
              <div className="flex items-center">
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {t(`furniture.condition.${item.condition}`)}
                  </span>
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    {t(`home.categories.${item.category}`)}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-900">{t('furniture.detail.description')}</h3>
              <div className="mt-2 prose prose-sm text-gray-500">
                <p>{item.description}</p>
              </div>
            </div>

            {item.dimensions && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-900">{t('furniture.detail.extraDetails')}</h3>
                <div className="mt-2">
                  <div className="text-sm text-gray-500 space-y-1">
                    <p>{t('furniture.detail.dimensions')}: {item.dimensions.width}" × {item.dimensions.height}" × {item.dimensions.depth}"</p>
                    {item.material && <p>{t('furniture.detail.material')}: {item.material.join(', ')}</p>}
                    {item.color && <p>{t('furniture.detail.color')}: {item.color.join(', ')}</p>}
                    {item.brand && <p>{t('furniture.detail.brand')}: {item.brand}</p>}
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6">
              <div className="flex items-center text-sm text-gray-500">
                <p>{t('furniture.detail.location')}: {item.location.city}, {item.location.state}</p>
                {item.neighborhood && (
                  <>
                    <span className="mx-2">•</span>
                    <p>{t('furniture.detail.neighborhood')}: {getNeighborhoodLabel(item.neighborhood, i18n.language)}</p>
                  </>
                )}
                <span className="mx-2">•</span>
                <p>{t('furniture.detail.pickupOnly')}</p>
              </div>
              {item.pickupDetails && (
                <p className="mt-2 text-sm text-gray-500">{t('furniture.detail.pickupDetails')}: {item.pickupDetails}</p>
              )}
              <div className="mt-4 space-y-3">
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                  {t('furniture.detail.logisticsNotice')}
                </p>
                <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3">
                  {t('furniture.detail.tradeNotice')}
                </p>
              </div>
            </div>

            {showContact && (
              <div className="mt-6 bg-indigo-50 border border-indigo-200 rounded-md p-4">
                <h3 className="text-sm font-medium text-gray-900">{t('furniture.detail.sellerContact')}</h3>
                {sellerInfo ? (
                  <div className="mt-2 text-sm text-gray-700">
                    <p>
                      {[sellerInfo.firstName, sellerInfo.lastName].filter(Boolean).join(' ') || t('furniture.detail.seller')}
                    </p>
                    {contactWechat && (
                      <p className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-700">{t('profile.wechatId')}: <span className="font-medium text-green-600">{contactWechat}</span></span>
                        <button
                          type="button"
                          onClick={handleCopyWechat}
                          className="text-xs bg-green-50 border border-green-200 text-green-700 px-2 py-1 rounded"
                        >
                          {copied ? t('furniture.detail.wechatCopied') : t('furniture.detail.copyWechat')}
                        </button>
                      </p>
                    )}
                    {typeof item.seller === 'object' && (
                      <div className="mt-2">
                        <EduBadge eduVerified={item.seller.eduVerified} school={item.seller.school} />
                      </div>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      {t('furniture.detail.contactSafetyNotice')}
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-700">
                    {t('furniture.detail.contactUnavailable')}
                  </p>
                )}
              </div>
            )}

            <div className="mt-8 flex flex-col space-y-4">
              <button
                type="button"
                onClick={handleContact}
                className="w-full bg-indigo-600 border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {t('furniture.detail.contactSeller')}
              </button>

              {contactWechat && (
                <button
                  type="button"
                  onClick={handleCopyWechat}
                  className="w-full bg-green-50 border border-green-300 rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-green-800 hover:bg-green-100"
                >
                  {copied ? t('furniture.detail.wechatCopied') : t('furniture.detail.copyWechat')}
                </button>
              )}
              
              <button
                  type="button"
                  onClick={handleSaveItem}
                  disabled={savingItem}
                  className={`w-full border rounded-md py-3 px-8 flex items-center justify-center text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                    isSaved 
                      ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100 focus:ring-red-500'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-indigo-500'
                  }`}
                >
                  {savingItem ? t('common.loading') : (isSaved ? t('furniture.detail.saved') : t('furniture.detail.saveItem'))}
                </button>
              {sellerInfo && user?.id !== sellerInfo._id && <button type="button" onClick={handleReport} className="text-sm text-red-700 underline">{t('furniture.detail.reportAccount')}</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FurnitureDetail;
